import type { InputNumberProps } from 'antd'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useFormatNumber } from 'core/hooks/useFormatNumber'
import { buildInputTypeConfigs, InputType } from './input-type'
import { getNumberSeparators, parseLocaleNumber } from './input-parsing-locale-number'

export type InputConfig = {
  decimals: number
  editStep: number
  // Precision of the stored value (e.g. 1 displayed decimal on a percentage => 3 decimals on the ratio).
  precision: number
  formatter: NonNullable<InputNumberProps<number>['formatter']>
  parser: NonNullable<InputNumberProps<number>['parser']>
}

export const useInputTypeConfig = (type: InputType): InputConfig => {
  const { i18n } = useTranslation()
  const separators = getNumberSeparators(i18n.resolvedLanguage ?? i18n.language)
  const formatNumber = useFormatNumber()

  const { decimals, scaleExponent, format } = useMemo(
    () => buildInputTypeConfigs({ formatNumber })[type],
    [formatNumber, type]
  )

  const precision = decimals + scaleExponent
  const editStep = 10 ** -precision

  const formatter = useCallback<InputConfig['formatter']>(
    (value, { userTyping, input }) => {
      // keep the raw input while typing so the cursor does not jump
      if (userTyping) {
        return input
      }
      if (value === undefined || value === null || `${value}` === '') {
        return ''
      }
      return format(Number(value), decimals, scaleExponent)
    },
    [format, decimals, scaleExponent]
  )

  const parser: InputConfig['parser'] = displayValue => parseLocaleNumber(displayValue ?? '', separators, scaleExponent)

  return { decimals, editStep, precision, formatter, parser }
}
